import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { AdminNotificationItem, AdminNotificationCategory } from '@/types/admin'

export const ADMIN_NOTIFICATIONS_QUERY_KEY = ['admin', 'notifications-feed'] as const

const LOOKBACK_USERS_MS = 48 * 60 * 60 * 1000
const LOOKBACK_LIST_MS = 7 * 24 * 60 * 60 * 1000

function truncate(s: string, max: number): string {
  const t = s.trim()
  if (t.length <= max) return t
  return `${t.slice(0, max - 1)}…`
}

export interface AdminNotificationsFeed {
  /** Merged, sorted by `createdAt` desc (newest first). */
  all: AdminNotificationItem[]
  byCategory: Record<AdminNotificationCategory, AdminNotificationItem[]>
  /** Total actionable items surfaced in header bell (reports + workflows pending + new users + appeals needing reply). */
  bellCount: number
  sidebarBadges: {
    banAppealsNeedingReply: number
    workflowsPendingReview: number
    userSupportNeedingReply: number
  }
}

async function fetchAdminNotifications(): Promise<AdminNotificationsFeed> {
  const sinceRecentUsers = new Date(Date.now() - LOOKBACK_USERS_MS).toISOString()
  const sinceLists = new Date(Date.now() - LOOKBACK_LIST_MS).toISOString()

  const [
    newProfilesRes,
    newUsersHead,
    pendingReportsListRes,
    reportsPendingHead,
    pendingWfListRes,
    pendingWfHeadRes,
    banMsgRes,
    supportMsgRes,
  ] = await Promise.all([
    supabase
      .from('profiles')
      .select('user_id, username, created_at, role')
      .gte('created_at', sinceRecentUsers)
      .order('created_at', { ascending: false })
      .limit(12),
    supabase.from('profiles').select('id', { count: 'exact', head: true }).gte('created_at', sinceRecentUsers),
    supabase
      .from('content_reports')
      .select('id, target_type, target_title, reason, created_at')
      .eq('status', 'pending')
      .gte('created_at', sinceLists)
      .order('created_at', { ascending: false })
      .limit(14),
    supabase.from('content_reports').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
    supabase
      .from('workflows')
      .select('id, title, created_at')
      .eq('status', 'pending_review')
      .gte('created_at', sinceLists)
      .order('created_at', { ascending: false })
      .limit(14),
    supabase.from('workflows').select('id', { count: 'exact', head: true }).eq('status', 'pending_review'),
    supabase
      .from('user_ban_support_messages')
      .select('user_id, sender, body, created_at')
      .order('created_at', { ascending: false })
      .limit(500),
    supabase
      .from('user_support_messages')
      .select('user_id, sender, body, created_at')
      .order('created_at', { ascending: false })
      .limit(500),
  ])

  const users: AdminNotificationItem[] = []
  for (const row of newProfilesRes.data ?? []) {
    users.push({
      id: `user-${row.user_id}`,
      category: 'users',
      title: `New signup: ${row.username}`,
      subtitle: row.role && row.role !== 'user' ? `Role ${row.role}` : 'Joined the platform',
      createdAt: row.created_at,
      href: '/admin/users',
    })
  }

  const reports: AdminNotificationItem[] = []
  for (const row of pendingReportsListRes.data ?? []) {
    reports.push({
      id: `report-${row.id}`,
      category: 'reports',
      title: truncate(row.target_title || `${row.target_type} report`, 56),
      subtitle: truncate(row.reason, 96),
      createdAt: row.created_at,
      href: '/admin/reports',
    })
  }

  const workflows: AdminNotificationItem[] = []
  for (const row of pendingWfListRes.data ?? []) {
    workflows.push({
      id: `wf-${row.id}`,
      category: 'workflows',
      title: 'Workflow awaiting approval',
      subtitle: truncate(row.title, 96),
      createdAt: row.created_at,
      href: `/admin/workflows?status=pending_review`,
    })
  }

  type ThreadMeta = { sender: string; body: string; created_at: string }

  function latestThreads(
    rows: { user_id: string; sender: string; body: string; created_at: string }[] | null,
  ): Map<string, ThreadMeta> {
    const m = new Map<string, ThreadMeta>()
    for (const row of rows ?? []) {
      if (!m.has(row.user_id)) {
        m.set(row.user_id, { sender: row.sender, body: row.body, created_at: row.created_at })
      }
    }
    return m
  }

  function needReplyIds(m: Map<string, ThreadMeta>): string[] {
    const ids: string[] = []
    for (const [uid, meta] of m) {
      if (meta.sender === 'user') ids.push(uid)
    }
    return ids
  }

  const banLatest = latestThreads(banMsgRes.data ?? null)
  const supportLatest = latestThreads(supportMsgRes.data ?? null)
  const banNeedReply = needReplyIds(banLatest)
  const supportNeedReply = needReplyIds(supportLatest)
  const nameLookupIds = [...new Set([...banNeedReply, ...supportNeedReply])]

  let unameByUid: Record<string, string> = {}
  if (nameLookupIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('user_id, username')
      .in('user_id', nameLookupIds)

    unameByUid = Object.fromEntries((profiles ?? []).map((p) => [p.user_id, p.username]))
  }

  const banAppeals: AdminNotificationItem[] = banNeedReply
    .map((uid) => {
      const meta = banLatest.get(uid)!
      return {
        id: `ban-${uid}-${meta.created_at}`,
        category: 'ban_appeals' as const,
        title: `Ban appeal: ${unameByUid[uid] ?? 'User'}`,
        subtitle: truncate(meta.body, 120),
        createdAt: meta.created_at,
        href: `/admin/ban-messages?thread=${encodeURIComponent(uid)}`,
      }
    })
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))

  const userSupportItems: AdminNotificationItem[] = supportNeedReply
    .map((uid) => {
      const meta = supportLatest.get(uid)!
      return {
        id: `support-${uid}-${meta.created_at}`,
        category: 'user_support' as const,
        title: `Help desk: ${unameByUid[uid] ?? 'User'}`,
        subtitle: truncate(meta.body, 120),
        createdAt: meta.created_at,
        href: `/admin/support?thread=${encodeURIComponent(uid)}`,
      }
    })
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))

  const byCategory: AdminNotificationsFeed['byCategory'] = {
    users,
    ban_appeals: banAppeals,
    user_support: userSupportItems,
    reports,
    workflows,
  }

  const all = [...users, ...banAppeals, ...userSupportItems, ...reports, ...workflows].sort((a, b) =>
    a.createdAt < b.createdAt ? 1 : -1,
  )

  const bellCount =
    (reportsPendingHead.count || 0) +
    (pendingWfHeadRes.count || 0) +
    (newUsersHead.count || 0) +
    banNeedReply.length +
    supportNeedReply.length

  const sidebarBadges = {
    banAppealsNeedingReply: banNeedReply.length,
    workflowsPendingReview: pendingWfHeadRes.count || 0,
    userSupportNeedingReply: supportNeedReply.length,
  }

  return { all, byCategory, bellCount, sidebarBadges }
}

export function useAdminNotifications(enabled = true) {
  return useQuery({
    queryKey: ADMIN_NOTIFICATIONS_QUERY_KEY,
    queryFn: fetchAdminNotifications,
    staleTime: 30 * 1000,
    enabled,
  })
}

/** Call after moderator actions so header/sidebar badges refresh quickly. */
export function useInvalidateAdminNotifications() {
  const qc = useQueryClient()
  return () =>
    qc.invalidateQueries({
      queryKey: ADMIN_NOTIFICATIONS_QUERY_KEY,
    })
}
